/* eslint-disable import/prefer-default-export */
import { setLoadingAction } from 'domains/control/actions'
import * as API from 'api/domains/admin/audits'
import { getCurrentUser } from 'api/domains/users/users'
import { isEmpty, reduce } from 'lodash'
import {
  SET_INITIAL_STATE,
  SET_QUERY,
  SET_AUDITS_PUBLISHED_DONE_PROGRESS,
} from './types'

export const updateAuditAction = (id, body, callbacks) => (dispatch) => {
  const onBefore = async () => dispatch(setLoadingAction(true))
  const onAfter = async () => dispatch(setLoadingAction(false))
  return API.updateAudit(id, body)({ onBefore, onAfter, ...callbacks })
}

const approveAuditAction = (id, status, callbacks) => (dispatch) =>
  getCurrentUser()({
    onSuccess: async (res) => {
      const userId = res.id
      dispatch(
        updateAuditAction(
          id,
          {
            audit: {
              status,
              approved_at: new Date(),
              approver_id: userId,
              approver_message: '',
            },
          },
          callbacks,
        ),
      )
    },
  })

let publishAllAuditsInFlight = null

const publishAuditResult = (audit) =>
  new Promise((resolve) => {
    let result

    API.publishAudit(audit.id, { perform_backup: true })({
      onSuccess: (response) => {
        result = { audit, success: true, response }
      },
      onFailure: (error) => {
        result = { audit, success: false, error }
      },
      onAfter: () => resolve(result),
    })
  })

export const publishAllAuditsAction =
  (audits, callbacks = {}) =>
  (dispatch) => {
    if (publishAllAuditsInFlight) return publishAllAuditsInFlight

    const publication = async () => {
      const results = []
      dispatch(setLoadingAction(true))

      try {
        for (const audit of audits) {
          // Publishing sequentially avoids concurrent writes to related cards.
          // eslint-disable-next-line no-await-in-loop
          const result = await publishAuditResult(audit)
          results.push(result)
          if (callbacks.onResult) await callbacks.onResult(result)
        }

        if (callbacks.onAfter) await callbacks.onAfter(results)
        return results
      } finally {
        dispatch(setLoadingAction(false))
      }
    }

    publishAllAuditsInFlight = publication().finally(() => {
      publishAllAuditsInFlight = null
    })

    return publishAllAuditsInFlight
  }

export const publishAuditAction =
  (id, performBackup, callbacks) => (dispatch) => {
    const onBefore = async () => {
      dispatch(setLoadingAction(true))
      if (callbacks.onBefore) await callbacks.onBefore()
    }
    const onAfter = async () => {
      dispatch(setLoadingAction(false))
      if (callbacks.onAfter) callbacks.onAfter()
    }

    API.publishAudit(id, { perform_backup: performBackup })({
      ...callbacks,
      onBefore,
      onAfter,
    })
  }

export const cancelApprovementAuditAction = (id, callbacks) => (dispatch) =>
  dispatch(
    updateAuditAction(
      id,
      {
        audit: {
          status: 'waiting_for_review',
          approved_at: null,
          approver_id: null,
        },
      },
      callbacks,
    ),
  )

export const acceptAuditAction = (auditId, callbacks) => (dispatch) =>
  dispatch(approveAuditAction(auditId, 'accepted', callbacks))

export const rejectAuditAction = (auditId, data, callbacks) => (dispatch) =>
  getCurrentUser()({
    onSuccess: async (res) => {
      const userId = res.id
      dispatch(
        updateAuditAction(
          auditId,
          {
            audit: {
              status: 'rejected',
              approved_at: new Date(),
              approver_id: userId,
              ...data,
            },
          },
          callbacks,
        ),
      )
    },
  })

export const getAuditsWithSearchAction =
  (query, callbacks = {}) =>
  (dispatch) => {
    const onBefore = async () => {
      dispatch(setLoadingAction(true))
      if (callbacks.onBefore) await callbacks.onBefore()
    }
    const onAfter = async () => {
      dispatch(setLoadingAction(false))
      if (callbacks.onAfter) callbacks.onAfter()
    }

    const resQuery = reduce(
      query,
      (res, value, key) => {
        if (!isEmpty(value) || value === true || value === false) {
          res[`q[${key}]`] = value
        }
        return res
      },
      {},
    )

    if (isEmpty(resQuery)) {
      return callbacks.onEmptyQuery()
    }

    return API.getAuditsWithSearch({ ...resQuery, per_page: 500 })({
      onBefore,
      onAfter,
      ...callbacks,
    })
  }

export const setInitialStateAction = () => ({
  type: SET_INITIAL_STATE,
})

export const setQueryAction = (payload) => ({
  type: SET_QUERY,
  payload,
})

export const setAuditsPublishedDoneProgressAction = (payload) => ({
  type: SET_AUDITS_PUBLISHED_DONE_PROGRESS,
  payload,
})
